import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import PetPassport from "@/pages/PetPassport";
import PublicPassport from "@/pages/PublicPassport";
import TravelMode from "@/pages/TravelMode";
import EmergencyMode from "@/pages/EmergencyMode";
import Settings from "@/pages/Settings";
import AdminDashboard from "@/pages/AdminDashboard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import ResetPassword from "@/pages/ResetPassword";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAF9F6" }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-[#FF8A65] border-t-transparent animate-spin" />
          <p className="font-['Figtree'] text-slate-500">Loading your pet world...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAF9F6" }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-[#FF8A65] border-t-transparent animate-spin" />
          <p className="font-['Figtree'] text-slate-500">Loading…</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Synchronously detect session_id in hash before rendering routes (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/passport/share/:shareToken" element={<PublicPassport />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/passport/:petId" element={<ProtectedRoute><PetPassport /></ProtectedRoute>} />
      <Route path="/travel" element={<ProtectedRoute><TravelMode /></ProtectedRoute>} />
      <Route path="/emergency" element={<ProtectedRoute><EmergencyMode /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
